import { create } from 'zustand';
import type { PlatformRole } from '@/lib/abilities';

export interface PlatformUser {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    role: PlatformRole;
}

/** Server-side feature switches from GET /auth/me `features` (absent = unknown = treat as on). */
export type PlatformFeatureName = 'impersonation' | 'chat';
export type PlatformFeatures = Partial<Record<PlatformFeatureName, boolean>>;

interface AuthState {
    accessToken: string | null;
    user: PlatformUser | null;
    setAccessToken: (token: string | null) => void;
    setUser: (user: PlatformUser | null) => void;
    setSession: (args: { accessToken: string; user: PlatformUser }) => void;
    reset: () => void;
    /** Why the last session ended; 'expired' when refresh failed mid-session (UX-10). */
    sessionEndReason: 'expired' | null;
    /** Clear the session because it expired (refresh failed while signed in). */
    expireSession: () => void;
    /** null until /auth/me has been read this session. */
    features: PlatformFeatures | null;
    setFeatures: (features: PlatformFeatures) => void;
    /** Silent disable after the backend answered PLATFORM_FEATURE_DISABLED. */
    disableFeature: (name: PlatformFeatureName) => void;
    /** BE-07: the operator must set a new password before using the console. */
    passwordChangeRequired: boolean;
    setPasswordChangeRequired: (required: boolean) => void;
}

/**
 * Auth state lives in memory ONLY. This Zustand store intentionally
 * does NOT use persist() — access tokens must not land in
 * localStorage/sessionStorage (CLAUDE.md §3 / §9.5). On reload the
 * store starts empty; the app calls /auth/refresh using the httpOnly
 * cookie to rehydrate.
 *
 * The refresh cookie is httpOnly, so JS cannot tell whether one exists.
 * A boolean session hint (no token, no identifier) is kept in
 * localStorage so a visitor who never signed in on this browser does not
 * fire a guaranteed-401 /auth/refresh on page load.
 */
const SESSION_HINT_KEY = 'tajergo_platform_session_hint';

function writeSessionHint(present: boolean): void {
    try {
        if (present) window.localStorage.setItem(SESSION_HINT_KEY, '1');
        else window.localStorage.removeItem(SESSION_HINT_KEY);
    } catch {
        // Storage unavailable (SSR, privacy mode) - refresh is simply attempted.
    }
}

export function hasPlatformSessionHint(): boolean {
    try {
        return window.localStorage.getItem(SESSION_HINT_KEY) === '1';
    } catch {
        return true;
    }
}

export const useAuthStore = create<AuthState>((set) => ({
    accessToken: null,
    user: null,
    setAccessToken: (accessToken) => {
        writeSessionHint(Boolean(accessToken));
        set({ accessToken });
    },
    setUser: (user) => set({ user }),
    setSession: ({ accessToken, user }) => {
        writeSessionHint(true);
        set({ accessToken, user, sessionEndReason: null });
    },
    reset: () => {
        writeSessionHint(false);
        set({ accessToken: null, user: null, features: null, passwordChangeRequired: false });
    },
    sessionEndReason: null,
    expireSession: () => {
        writeSessionHint(false);
        set({ accessToken: null, user: null, features: null, sessionEndReason: 'expired', passwordChangeRequired: false });
    },
    features: null,
    setFeatures: (features) => set({ features }),
    disableFeature: (name) => set((state) => ({ features: { ...(state.features ?? {}), [name]: false } })),
    passwordChangeRequired: false,
    setPasswordChangeRequired: (passwordChangeRequired) => set({ passwordChangeRequired }),
}));

/**
 * BE-07: true when the access token carries the `pwc` claim (signed in with a
 * temporary password). Display routing only; the backend enforces it.
 */
export function accessTokenRequiresPasswordChange(token: string | null | undefined): boolean {
    const part = token?.split('.')[1];
    if (!part) return false;
    try {
        const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
        return (JSON.parse(json) as { pwc?: unknown }).pwc === true;
    } catch {
        return false;
    }
}


/** False only when the server said the feature is off; unknown counts as on. */
export function isPlatformFeatureEnabled(name: PlatformFeatureName): boolean {
    return useAuthStore.getState().features?.[name] !== false;
}

/** Hook form of isPlatformFeatureEnabled, re-renders when the switch changes. */
export function usePlatformFeatureEnabled(name: PlatformFeatureName): boolean {
    return useAuthStore((s) => s.features?.[name] !== false);
}
